// ARKit-52 -> this bundle's control vector, through the stages IN THE ORDER THAT MATTERS.
// Notes: ~/comments/aos-threejs-poc/faceRigComposer_composeRig_stage_order.md

import { applyLiveMouthMix } from "../rig/liveRigRegions.js";
import { arkitToIsaacRig, applyEyeFollowRig } from "./rigMath.ts";
import { BLINK_LEFT, BLINK_RIGHT } from "./arkitFrame.ts";
import type { RigConstraintSet } from "./rigConstraints.ts";

export interface ComposeRigInput {
  /** ARKit-52 -> this control space (rigGatherFromNames). */
  gather: ArrayLike<number>;
  /** 188-float reusable buffer — the MetaHuman intermediate. */
  scratch: Float32Array;
  constraints: RigConstraintSet;
  /** __livelink__ weight: the live mouth mix applies to SPOKEN frames only. */
  liveWeight: number;
  /** Rig Controller weight: an authored pose is not mixed toward the manifold. */
  rigWeight: number;
  /** Eye-follow's ARKit overlay, or null when the aim is not driving. */
  eyeOverlay?: ArrayLike<number> | null;
}

export interface ComposedRig {
  /** The aperture coordinate the live mouth mix used, for the debug readout. */
  mouthT: number;
  /** Which stages actually ran this frame. */
  stages: { mouthMix: boolean; blink: boolean; subspace: boolean; eyeFollow: boolean };
}

/**
 * Compose `out` (length === gather.length). Allocation-free.
 *
 * FOUR STAGES, IN THIS ORDER, and the order is load-bearing — see the note.
 */
export function composeRig(arkit: Float32Array, input: ComposeRigInput, out: Float32Array): ComposedRig {
  const { constraints: c } = input;
  const stages = { mouthMix: false, blink: false, subspace: false, eyeFollow: false };

  // 0. ARKit -> the control space, composed onto the trained base and clamped.
  arkitToIsaacRig(arkit, input.gather, out, input.scratch, c.arkitBase, c.range);

  // 1. Trained-manifold mouth mix, LIVE FRAMES ONLY: pull the mouth block toward the
  //    interpolation between this bundle's trained closed/open references, with the A2F
  //    jaw picking the coordinate. Clips and Rig Preview frames are authored poses.
  let mouthT = 0;
  if (input.liveWeight > 0 && input.rigWeight === 0 && c.mouthDrive && c.mouthWeight > 0) {
    mouthT = applyLiveMouthMix(out, c.mouthDrive, c.mouthWeight);
    stages.mouthMix = true;
  }

  // 2. BLINK BEFORE THE PROJECTION. ARKit drives one control per eye while every trained
  //    blink row moves the whole lid group, so the raw request is off the manifold and the
  //    projection halves it. Driving the group makes it a real configuration, which the
  //    projection then leaves alone.
  if (c.blinkMacro) {
    c.blinkMacro.applyInto(out, arkit[BLINK_LEFT], arkit[BLINK_RIGHT]);
    stages.blink = true;
  }

  // 3. TRAINED SUBSPACE, LAST of the three, so it verifies whatever they produced. Stage 1's
  //    output is NOT automatically inside the span — it blends a SUBSET partway toward an
  //    interpolation, which is not a convex combination of trained rows over every control.
  if (c.subspace) {
    c.subspace.projectInto(out);
    stages.subspace = true;
  }

  // 4. EYE FOLLOW, after all three. On a bundle whose trained data freezes the eye controls
  //    the clamp pins them to rest and the projection's basis has no eye variance to keep, so
  //    anything written earlier is ERASED. Writing here trades that for extrapolation on the
  //    aim controls alone, bounded by the DECLARED range — never the trained box.
  if (input.eyeOverlay && c.eyeFollow) {
    const range = c.declaredRange;
    applyEyeFollowRig(out, c.eyeFollow, input.eyeOverlay, range ? range[0] : -Infinity, range ? range[1] : Infinity);
    stages.eyeFollow = true;
  }

  return { mouthT, stages };
}

/** True when `rig` differs from `last` by more than the push threshold. */
export function rigChanged(rig: Float32Array, last: Float32Array | null, epsilon = 1e-4): boolean {
  if (!last || last.length !== rig.length) return true;
  for (let i = 0; i < rig.length; i++) if (Math.abs(rig[i] - last[i]) > epsilon) return true;
  return false;
}
